import { Box, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import {
  CostBreakdownActions,
  CostBreakdownCompletedCell,
  CostBreakdownQuantityCell,
} from "../components/CostBreakdownTable"

import { ProductStatusCell, PurchaseOrderNumberCell } from "@/components/table"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { formatCostBreakdownCurrency } from "@/screens/Procurement/CostBreakdown/utils/formatCostBreakdownCurrency"
import type { PurchaseOrderLineWithReceiptContext } from "@/screens/WarehouseReceiving/types"

interface UseCostBreakdownPurchaseOrderColumnsProps {
  goodsReceipt: GoodsReceipt
  canExpandIncompleteRows?: boolean
  canMaintain?: boolean
  onEdit?: (goodsReceipt: GoodsReceipt, purchaseOrderLineId: string) => void
}

export const useCostBreakdownPurchaseOrderColumns = ({
  goodsReceipt,
  canExpandIncompleteRows = false,
  canMaintain = false,
  onEdit,
}: UseCostBreakdownPurchaseOrderColumnsProps): ColumnDef<PurchaseOrderLineWithReceiptContext>[] => {
  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  return useMemo<ColumnDef<PurchaseOrderLineWithReceiptContext>[]>(() => {
    return [
      {
        accessorKey: "orderId",
        header: "PO #",
        cell: ({ row }) => {
          const order = row.original.orderDetail?.order
          return order ? <PurchaseOrderNumberCell order={order} /> : null
        },
      },
      {
        accessorKey: "productId",
        header: "Product ID",
        cell: ({ row }) => {
          const product = row.original.product
          return product ? <ProductStatusCell product={product} /> : null
        },
      },
      {
        accessorKey: "akrochemDescription",
        header: "Akrochem Description",
        cell: ({ row }) => {
          const product = row.original.product
          return product?.companyProductDescription ?? ""
        },
      },
      {
        accessorKey: "quantity",
        header: "Qty Received",
        cell: ({ row }) => <CostBreakdownQuantityCell purchaseOrderLine={row.original} />,
      },
      {
        accessorKey: "totalLaidDownCost",
        header: "Total Laid-Down Cost per UoM",
        cell: ({ row }) => formatCostBreakdownCurrency(row.original.costBreakdown?.totalLaidDownCostPerUom),
      },
      {
        accessorKey: "completed",
        header: "Completed",
        cell: ({ row }) => <CostBreakdownCompletedCell completed={row.original.costBreakdown?.isCompleted} />,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        size: 70,
        cell: ({ row }) => {
          const isCompleted = row.original.costBreakdown?.isCompleted || false
          const isExpanderVisible = canExpandIncompleteRows || isCompleted

          return (
            <Box component='span' className='flex items-center justify-end gap-x-3' onClick={handleStopPropagation}>
              {canMaintain && onEdit && (
                <CostBreakdownActions
                  goodsReceipt={goodsReceipt}
                  isProductLevel
                  testIdPrefix='product-cost-breakdown'
                  onEdit={() => onEdit(goodsReceipt, row.original.orderLineId)}
                />
              )}
              {isExpanderVisible ? (
                <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
                  {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
                </IconButton>
              ) : (
                <span className='w-[34px] shrink-0' />
              )}
            </Box>
          )
        },
      },
    ]
  }, [canExpandIncompleteRows, canMaintain, goodsReceipt, onEdit, handleStopPropagation])
}
